import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import PdfEditPage from '../components/PdfEditPage';
import s, { PdfEditResponseStruct } from '../utils/struct';
import useApiRequest from '../utils/useApiRequest';
import { apiUrl } from '../utils/apiRequest';
import getSelectedPages from '../utils/getSelectedPages';
import getErrorName from '../utils/getErrorName';
import { sendEvent } from '../utils/analytics';

const PageSplitPdf = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'split-pdf']);
  const [fileTable, setFileTable] = useState(null);

  const {
    sendApiRequest,
    processing,
    error,
  } = useApiRequest({ url: '/api/pdf/split' });

  const handleSubmit = useCallback(({ pdfFiles, separate }) => {
    if (processing) {
      return;
    }

    sendApiRequest({
      data: {
        files: pdfFiles.map((pdfFile) => ({
          name: pdfFile.file.file.name,
          filename: pdfFile.file.hash,
          pages: getSelectedPages(pdfFile),
          password: pdfFile.file.password,
          separate,
        })),
      },
      validateResponse(response) {
        s.assert(response.data, PdfEditResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'pdf-split',
          action: 'success',
          label: `${time}:${pdfFiles.length}`,
        });

        const firstFile = pdfFiles[0].file;
        const firstResultFiles = response.data.result?.[firstFile.hash]?.files;
        let fileType;
        let fileName;
        let fileSize;
        let fileUrl;

        if (pdfFiles.length > 1 || firstResultFiles.length > 1) {
          const { url, size } = response.data.result.zip;

          fileType = 'zip';
          fileUrl = url;
          fileName = 'files-split-pdf.zip';
          fileSize = size;
        } else {
          const { url, size } = firstResultFiles[0];

          fileType = 'pdf';
          fileUrl = url;
          fileName = firstFile.file.name;
          fileSize = size;
        }

        setFileTable({
          fileType,
          fileName,
          fileSize,
          fileUrl: apiUrl(fileUrl),
          compressable: false, // TODO: make compressable
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'pdf-split',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${pdfFiles.length}`,
        });
      },
    });
  }, [processing, sendApiRequest]);

  const meta = useMemo(() => ({
    title: t('split-pdf:meta.title'),
    description: t('split-pdf:meta.description'),
  }), [t]);

  const intro = useMemo(() => ({
    title: t('split-pdf:intro.title'),
    text: t('split-pdf:intro.text'),
  }), [t]);

  const dropzone = useMemo(() => ({
    options: {
      multiple: true,
      maxFiles: 10,
    },
  }), []);

  const howTo = useMemo(() => ({
    title: t('split-pdf:howto.title'),
    steps: t('split-pdf:howto.steps', { returnObjects: true }),
  }), [t]);

  const features = useMemo(() => ({
    content: t('split-pdf:features.content', { returnObjects: true }),
  }), [t]);

  const faq = useMemo(() => ({
    items: t('split-pdf:faq.items', { returnObjects: true }),
  }), [t]);

  const pdfViewer = useMemo(() => ({
    title: t('common:nav.split-pdf'),
    viewType: 'pages',
    showViewSwitcher: false,
    separatable: true,
    selectable: true,
    deletablePages: true,
    processing,
  }), [t, processing]);

  const actionsGrid = useMemo(() => ({
    rotate: true,
    unlock: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }), []);

  return (
    <PdfEditPage
      meta={meta}
      breadcrumb={t('common:nav.split-pdf')}
      intro={intro}
      dropzone={dropzone}
      features={features}
      faq={faq}
      pdfViewer={pdfViewer}
      fileTable={fileTable}
      actionsGrid={actionsGrid}
      onSubmit={handleSubmit}
      submitError={error}
      howTo={howTo}
    />
  );
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'split-pdf',
    ]),
  },
});

export default PageSplitPdf;
